/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Key } from "react";
import { Element, Node, Text } from "slate";
import { RenderElementProps, RenderLeafProps } from "slate-react";
import Button from "../../components/lib/Button/Button";

export interface NodeProps extends Element {
  url?: string;
}

const blockTypes = [
  "blockquote",
  "heading-1",
  "heading-2",
  "heading-3",
  "heading-4",
  "heading-5",
  "heading-6",
  "button",
  "ordered-list",
  "unordered-list",
  "list-item",
  "paragraph",
];

const renderedElement = (
  element: Element | NodeProps,
  key: Key,
  children: any,
  attributes: any
) => {
  switch (element.type) {
    case "blockquote":
      return (
        <blockquote
          className="py-4 pl-4 border-l-4 border-gray-400"
          {...attributes}
          key={key}
        >
          {children}
        </blockquote>
      );
    case "heading-1":
      return (
        <h1 {...attributes} className={`h1 ${element.className}`} key={key}>
          {children}
        </h1>
      );
    case "heading-2":
      return (
        <h2 {...attributes} className={`h2 ${element.className}`} key={key}>
          {children}
        </h2>
      );
    case "heading-3":
      return (
        <h3 {...attributes} className={`h3 ${element.className}`} key={key}>
          {children}
        </h3>
      );
    case "heading-4":
      return (
        <h4 {...attributes} className={`h4 ${element.className}`} key={key}>
          {children}
        </h4>
      );
    case "heading-5":
      return (
        <h5 {...attributes} className={`h5 ${element.className}`} key={key}>
          {children}
        </h5>
      );
    case "heading-6":
      return (
        <h6 {...attributes} className={`h6 ${element.className}`} key={key}>
          {children}
        </h6>
      );
    case "button":
      if (Object.keys(attributes).length > 0) {
        return (
          <div
            {...attributes}
            className={`btn ${
              element.buttonType === "primary" ? "btn-primary" : "btn-outline"
            } ${element.className}`}
            key={key}
          >
            {children}
          </div>
        );
      } else {
        return (
          <Button
            variant="primary"
            className={`${element.className}`}
            key={key}
            type="submit"
          >
            {children}
          </Button>
        );
      }
    case "bulleted-list":
      return (
        <ul {...attributes} className={`${element.className}`} key={key}>
          {children}
        </ul>
      );
    case "list-item":
      return (
        <li {...attributes} className={`${element.className}`} key={key}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol {...attributes} className={`${element.className}`} key={key}>
          {children}
        </ol>
      );
    default:
      return (
        <p {...attributes} className={`${element.className}`} key={key}>
          {children}
        </p>
      );
  }
};

const renderedLeaf = (leaf: Text, key: Key, children: any, attributes: any) => {
  const classNames = [];
  if (leaf.color) classNames.push(leaf.color);
  if (leaf.fontSize) classNames.push(leaf.fontSize);
  if (leaf.fontWeight) classNames.push(leaf.fontWeight);
  if (leaf.bold) classNames.push("font-bold");
  if (leaf.italic) classNames.push("italic");
  if (leaf.underline) classNames.push("underline");
  if (leaf.code) {
    return (
      <code {...attributes} className={classNames.join(" ")} key={key}>
        {children}
      </code>
    );
  }
  return (
    <span {...attributes} className={classNames.join(" ")} key={key}>
      {children}
    </span>
  );
};

export const renderElement = ({
  attributes,
  children,
  element,
}: RenderElementProps, key: Key = "x") => {
  return renderedElement(element, key, children, attributes);
};
export const renderLeaf = ({ attributes, children, leaf }: RenderLeafProps, key: Key = "x") => {
  return renderedLeaf(leaf, key, children, attributes);
};

export const renderJSX = (element: Node, key: Key = "x") => {
  if (Text.isText(element)) {
    return element.text;
  }
  const children: any = element.children.map((n, index) => {
    if (blockTypes.includes(`${n.type}`)) {
      return renderJSX(n, index.toString());
    } else {
      return renderJSXLeaf(n, index.toString());
    }
  });
  return renderedElement(element, key, children, {});
};

const renderJSXLeaf = (leaf: Node, key: Key) => {
  if (Text.isText(leaf)) {
    if (Object.keys(leaf).length === 1 && leaf.text) {
      return leaf.text;
    }
    return renderedLeaf(leaf, key, leaf.text, {});
  } else {
    return "Ungültig";
  }
};
